import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { addError } from '../../../store/Error';
import { isNotFoundRefusal } from '../../../utils/refusalMessage';

/**
 * An engagement's data that is edited in place and saved in the background: each load waits for the edits still
 * saving, and an edit shown meanwhile makes it load again rather than put back what that edit replaced.
 */
export const useLoadAfterSaves = <T>(
  engagementId: string | null,
  load: (engagementId: string) => Promise<{ data: T | null; error: { message: string } | null }>,
  flushSaves: () => Promise<boolean>
) => {
  const [data, setData] = useState<T | null>(null);
  const [failed, setFailed] = useState(false);
  // A load that started before the user's latest change would put back what that change replaced.
  const edits = useRef(0);
  const loadedFor = useRef(engagementId);
  const shown = useRef(false);
  // Loads can overlap, and only the latest may answer.
  const latest = useRef(0);

  useEffect(() => {
    loadedFor.current = engagementId;
    shown.current = false;
    setData(null);
    setFailed(false);
  }, [engagementId]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!engagementId) return;
    const request = ++latest.current;
    const current = () => loadedFor.current === engagementId && request === latest.current;
    setFailed(false);
    // Counted before the flush: an edit made while it waits is in no save yet, so a load must not replace it.
    const editsBefore = edits.current;
    // A failed save stays queued for retry, so the server's older value must not replace what the user sees. With
    // nothing shown yet there is no edit to protect, so a failing save elsewhere doesn't keep the tab empty.
    if (!(await flushSaves()) && shown.current) {
      if (current()) setFailed(true);
      return;
    }
    const { data: loaded, error } = await load(engagementId);
    if (!current()) return;
    // An edit made meanwhile is newer than this answer, so load again once that edit has saved.
    if (edits.current !== editsBefore) return refresh();
    if (error || !loaded) {
      addError('advise-engagement', error?.message);
      setFailed(true);
    } else {
      shown.current = true;
      setData(loaded);
    }
  }, [engagementId, load, flushSaves]);

  // Shows an edit at once; a load already on its way then loads again instead of undoing it.
  const showEdit = useCallback((change: (prev: T) => T) => {
    edits.current += 1;
    setData((prev) => prev && change(prev));
  }, []);

  // False once the engagement has changed since the caller's request started.
  const isLoadedFor = useCallback((id: string) => loadedFor.current === id, []);

  // What a save names may have gone since it was shown, and answers 404 for good: the save is dropped and the data
  // reloaded rather than retried.
  const saveOrReload = useCallback(
    (save: () => Promise<{ error: { message: string; errorResponse?: AxiosError } | null }>) => async () => {
      const result = await save();
      if (!isNotFoundRefusal(result.error)) return result;
      refresh();
      return { error: null };
    },
    [refresh]
  );

  return { data, failed, refresh, showEdit, isLoadedFor, saveOrReload };
};
